/**
 * useFirestoreSnapshot — real-time Firestore hook with offline cache awareness
 *
 * Uses onSnapshot instead of getDocs to get real-time updates and
 * metadata about whether data is from cache or server.
 *
 * @param {Function} queryFn - Function returning a Firestore Query or null
 * @param {Array} deps - Dependency array (re-subscribes when these change)
 * @returns {{ data: Array, loading: boolean, error: string|null, fromCache: boolean }}
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { onSnapshot } from 'firebase/firestore';

const STALE_DELAY_MS = 2000;

const useFirestoreSnapshot = (queryFn, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const staleTimer = useRef(null);

  const buildQuery = useCallback(queryFn, deps);

  useEffect(() => {
    const q = buildQuery();
    if (!q) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setData(docs);
        setLoading(false);

        // Only show fromCache after a delay to avoid flashing during
        // normal cache-then-server snapshot sequences
        clearTimeout(staleTimer.current);
        if (snapshot.metadata.fromCache) {
          staleTimer.current = setTimeout(() => setFromCache(true), STALE_DELAY_MS);
        } else {
          setFromCache(false);
        }
      },
      (err) => {
        const code = err.code || '';
        if (code === 'permission-denied' || code === 'PERMISSION_DENIED' || /permission/i.test(err.message)) {
          setError('permission-denied');
        } else {
          setError(err.message || 'An error occurred');
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      clearTimeout(staleTimer.current);
    };
  }, [buildQuery]);

  return { data, loading, error, fromCache };
};

export default useFirestoreSnapshot;
